import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaClipboardList, FaPlaneDeparture, FaCalendarAlt } from "react-icons/fa";
import { getConsultantDashboard } from "../../../api/consultantPortalApi";
import {
  PageHeader,
  KpiStrip,
  KpiCard,
  Panel,
  StatusBadge,
} from "../../../components/Portal/ui";

// Status -> Chakra colour scheme for StatusBadge dots (registered scales only).
const STATUS_SCHEME = {
  draft: "gray",
  submitted: "blue",
  approved: "green",
  rejected: "red",
};

const Dashboard = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConsultantDashboard();
      setData(response);
    } catch (error) {
      toast({
        title: t("consultantPortal.dashboard.toasts.loadFailedTitle"),
        description:
          error?.response?.data?.detail ||
          t("consultantPortal.dashboard.toasts.loadFailedDescription"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const monthName = (month) => {
    const names = t("consultantPortal.common.monthNames", { returnObjects: true });
    return Array.isArray(names) ? names[month - 1] || "" : "";
  };

  const statusLabel = (status) =>
    status
      ? t(`consultantPortal.dashboard.statusLabels.${status}`, status)
      : null;

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CP"
        eyebrow={t("consultantPortal.common.eyebrow", "Consultant Portal")}
        title={
          data
            ? t("consultantPortal.dashboard.welcomeMessage", { name: data.consultantName })
            : t("consultantPortal.dashboard.pageTitle")
        }
        actions={
          <>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Icon as={FaClipboardList} boxSize="11px" />}
              onClick={() => navigate("/portal/consultant/activity-reports")}
            >
              {t("consultantPortal.dashboard.fillTimesheet")}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon as={FaPlaneDeparture} boxSize="11px" />}
              onClick={() => navigate("/portal/consultant/leave-requests")}
            >
              {t("consultantPortal.dashboard.requestLeave")}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon as={FaCalendarAlt} boxSize="11px" />}
              onClick={() => navigate("/portal/consultant/calendar")}
            >
              {t("consultantPortal.dashboard.viewCalendar")}
            </Button>
          </>
        }
      />

      <Box p="20px">
        {loading ? (
          <Flex minH="320px" align="center" justify="center">
            <Spinner size="lg" color="portal.primary" />
          </Flex>
        ) : !data ? null : (
          <>
            <KpiStrip columns={{ base: 1, md: 3 }} mb="12px">
              <KpiCard
                label={t("consultantPortal.dashboard.currentMonthTimesheet")}
                value={
                  data.currentMonth.status
                    ? `${data.currentMonth.billableDays}d`
                    : "—"
                }
                hint={
                  data.currentMonth.status
                    ? `${monthName(data.currentMonth.month)} ${data.currentMonth.year} · ${data.currentMonth.nonBillableDays}d ${t("consultantPortal.dashboard.nonBillable")}`
                    : `${monthName(data.currentMonth.month)} ${data.currentMonth.year} · ${t("consultantPortal.dashboard.notSubmitted")}`
                }
                actions={
                  data.currentMonth.status ? (
                    <StatusBadge
                      label={statusLabel(data.currentMonth.status)}
                      colorScheme={STATUS_SCHEME[data.currentMonth.status] || "gray"}
                    />
                  ) : null
                }
              />
              <KpiCard
                label={t("consultantPortal.dashboard.pendingLeaveRequests")}
                value={data.pendingLeaveRequestsCount}
                hint={
                  data.pendingLeaveRequestsCount > 0
                    ? t("consultantPortal.dashboard.pendingLeaveDescription", {
                        count: data.pendingLeaveRequestsCount,
                      })
                    : t("consultantPortal.dashboard.noPendingLeave")
                }
              />
              <KpiCard
                label={t("consultantPortal.dashboard.currentAssignment")}
                value={
                  data.currentAssignment
                    ? data.currentAssignment.needTitle
                    : "—"
                }
                hint={
                  data.currentAssignment
                    ? data.currentAssignment.clientName
                    : t("consultantPortal.dashboard.noAssignment")
                }
                actions={
                  data.currentAssignment?.assignmentStatus ? (
                    <StatusBadge
                      label={data.currentAssignment.assignmentStatus}
                      colorScheme="blue"
                    />
                  ) : null
                }
              />
            </KpiStrip>

            <Flex
              gap="12px"
              align="stretch"
              direction={{ base: "column", lg: "row" }}
            >
              <Box flex="1.4" minW={0} display="flex">
                <Panel
                  w="100%"
                  title={t("consultantPortal.dashboard.recentTimesheets")}
                >
                  <Box p="14px">
                    {data.recentMonths.length === 0 ? (
                      <Text
                        color="portal.muted"
                        fontSize="13px"
                        sx={{ marginBottom: "0 !important" }}
                      >
                        {t("consultantPortal.dashboard.notSubmitted")}
                      </Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
                        {data.recentMonths.map((item) => (
                          <Box
                            key={`${item.year}-${item.month}`}
                            p="12px"
                            bg="portal.soft"
                            borderWidth="1px"
                            borderColor="portal.line"
                            borderRadius="10px"
                          >
                            <Text
                              fontWeight="800"
                              fontSize="13px"
                              color="portal.text"
                              sx={{ marginBottom: "0 !important" }}
                            >
                              {monthName(item.month)} {item.year}
                            </Text>
                            {item.status ? (
                              <>
                                <Text
                                  fontSize="12px"
                                  fontWeight="600"
                                  color="portal.muted"
                                  mt="4px"
                                  sx={{ marginBottom: "0 !important" }}
                                >
                                  {item.billableDays}d {t("consultantPortal.dashboard.billable")},{" "}
                                  {item.nonBillableDays}d {t("consultantPortal.dashboard.nonBillable")}
                                </Text>
                                <Box mt="8px">
                                  <StatusBadge
                                    label={statusLabel(item.status)}
                                    colorScheme={STATUS_SCHEME[item.status] || "gray"}
                                  />
                                </Box>
                              </>
                            ) : (
                              <Text
                                fontSize="12px"
                                fontWeight="600"
                                color="portal.muted"
                                mt="4px"
                                sx={{ marginBottom: "0 !important" }}
                              >
                                {t("consultantPortal.dashboard.notSubmitted")}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                </Panel>
              </Box>

              <Box flex="1" minW={0} display="flex">
                <Panel
                  w="100%"
                  title={t("consultantPortal.dashboard.quickActions")}
                >
                  <Flex direction="column" gap="8px" p="14px">
                    <Button
                      variant="primary"
                      size="sm"
                      justifyContent="flex-start"
                      leftIcon={<Icon as={FaClipboardList} boxSize="12px" />}
                      onClick={() => navigate("/portal/consultant/activity-reports")}
                    >
                      {t("consultantPortal.dashboard.fillTimesheet")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      justifyContent="flex-start"
                      leftIcon={<Icon as={FaPlaneDeparture} boxSize="12px" />}
                      onClick={() => navigate("/portal/consultant/leave-requests")}
                    >
                      {t("consultantPortal.dashboard.requestLeave")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      justifyContent="flex-start"
                      leftIcon={<Icon as={FaCalendarAlt} boxSize="12px" />}
                      onClick={() => navigate("/portal/consultant/calendar")}
                    >
                      {t("consultantPortal.dashboard.viewCalendar")}
                    </Button>
                  </Flex>
                </Panel>
              </Box>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
